import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { createFrontendPlugin, PageBlueprint } from '@backstage/frontend-plugin-api';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { CatalogGraphPage, catalogGraphRouteRef } from '@backstage/plugin-catalog-graph';
import {
  EntityKindPicker,
  EntityOwnerPicker,
  EntityTypePicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { HomepageCompositionRoot, homePlugin } from '@backstage/plugin-home';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage, searchPlugin } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage, userSettingsPlugin } from '@backstage/plugin-user-settings';
import {
  CustomKindsPicker,
  EntityColumCustomizer, 
} from '@internal/plugin-ea-pack-react';
import { AdoProjectAndRepoPicker, AdoProjectPicker } from '@internal/backstage-plugin-scaffolder-ado-login';
import { CustomizableHomePage } from '../components/home/CustomizableHomePage';
import { searchPage } from '../components/search/SearchPage';

const SearchPageRoute = () => (
  <Routes>
    <Route element={<SearchPage />}>
      <Route index element={searchPage} />
    </Route>
  </Routes>
);

export const heliosCustomPagesPlugin = createFrontendPlugin({
  pluginId: 'helios-custom-pages',
  extensions: [
    PageBlueprint.make({
      name: 'root-redirect',
      params: {
        path: '/',
        loader: async () => <Navigate to="/home" replace />,
      },
    }),
    PageBlueprint.make({
      name: 'home',
      params: {
        path: '/home',
        routeRef: homePlugin.routes.root,
        loader: async () => (
          <HomepageCompositionRoot>
            <CustomizableHomePage />
          </HomepageCompositionRoot>
        ),
      },
    }),
    PageBlueprint.make({
      name: 'catalog',
      params: {
        path: '/catalog',
        routeRef: catalogPlugin.routes.catalogIndex,
        loader: async () => (
          <CatalogIndexPage
            columns={EntityColumCustomizer}
            ownerPickerMode="all"
            initialKind="system"
            actions={[]}
            filters={
              <>
                <CustomKindsPicker />
                <EntityKindPicker hidden initialFilter="system" />
                <EntityTypePicker />
                <UserListPicker />
                <EntityOwnerPicker />
              </>
            }
          />
        ),
      },
    }),
    PageBlueprint.make({
      name: 'docs',
      params: {
        path: '/docs',
        routeRef: techdocsPlugin.routes.root,
        loader: async () => <TechDocsIndexPage />,
      },
    }),
    PageBlueprint.make({
      name: 'docs-reader',
      params: {
        path: '/docs/:namespace/:kind/:name',
        routeRef: techdocsPlugin.routes.docRoot,
        loader: async () => (
          <TechDocsReaderPage>
            <TechDocsAddons>
              <ReportIssue />
            </TechDocsAddons>
          </TechDocsReaderPage>
        ),
      },
    }),
    PageBlueprint.make({
      name: 'create',
      params: {
        path: '/create',
        routeRef: scaffolderPlugin.routes.root,
        loader: async () => (
          <ScaffolderPage
            templateFilter={template =>
              !template.metadata.tags?.includes('hidden')}
          >
            <ScaffolderFieldExtensions>
              <AdoProjectAndRepoPicker />
              <AdoProjectPicker />
            </ScaffolderFieldExtensions>
          </ScaffolderPage>
        ),
      },
    }),
    // PageBlueprint.make({
    //   name: 'api-docs',
    //   params: {
    //     path: '/apis-docs',
    //     routeRef: apiDocsPlugin.routes.root,
    //     loader: async () => <ApiExplorerPage />,
    //   },
    // }),
    // PageBlueprint.make({
    //   name: 'catalog-import',
    //   params: {
    //     path: '/catalog-import',
    //     routeRef: catalogImportPlugin.routes.importPage,
    //     loader: async () => (
    //       <RequirePermission permission={catalogEntityCreatePermission}>
    //         <CatalogImportPage />
    //       </RequirePermission>
    //     ),
    //   },
    // }),
    PageBlueprint.make({
      name: 'search',
      params: {
        path: '/search',
        routeRef: searchPlugin.routes.root,
        loader: async () => <SearchPageRoute />,
      },
    }),
    PageBlueprint.make({
      name: 'settings',
      params: {
        path: '/settings',
        routeRef: userSettingsPlugin.routes.settingsPage,
        loader: async () => <UserSettingsPage />,
      },
    }),
    PageBlueprint.make({
      name: 'catalog-graph',
      params: {
        path: '/catalog-graph',
        routeRef: catalogGraphRouteRef,
        loader: async () => <CatalogGraphPage />,
      },
    }),
  ],
});
